import { Box, Button } from "@chakra-ui/react";
import { $patchStyleText } from "@lexical/selection";
import { $getSelection, $isRangeSelection, LexicalEditor } from "lexical";
import { ChangeEvent, useCallback, useRef } from "react";
import { RiFontColor, RiPaintFill } from "react-icons/ri";

type ColorStyle = "background-color" | "color";

type Props = {
  disabled?: boolean;
  editor: LexicalEditor;
  styleProperty: ColorStyle;
  value: string;
};

const ColorPicker = ({
  disabled = false,
  editor,
  styleProperty,
  value,
}: Props) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const isBackgroundColor = styleProperty === "background-color";
  const label = isBackgroundColor ? "Background color" : "Text color";

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const color = event.target.value;
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          $patchStyleText(selection, { [styleProperty]: color });
        }
      });
    },
    [editor, styleProperty]
  );

  return (
    <Box position="relative">
      <Button
        aria-label={label}
        colorScheme="gray"
        isDisabled={disabled}
        onClick={() => inputRef.current?.click()}
        padding="0"
        size="sm"
        title={label}
        variant="ghost"
      >
        <Box position="relative" lineHeight="0">
          {isBackgroundColor ? <RiPaintFill /> : <RiFontColor />}
        </Box>
      </Button>
      <input
        ref={inputRef}
        aria-label={`${label} picker`}
        disabled={disabled}
        onChange={handleChange}
        style={{
          height: 1,
          opacity: 0,
          position: "absolute",
          width: 1,
          right: 0,
          bottom: 0,
        }}
        tabIndex={-1}
        type="color"
        value={value}
      />
    </Box>
  );
};

export default ColorPicker;
